import { FunctionComponent, ImgHTMLAttributes, ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';

import Loader from '@/components/molecules/loader';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { Nullable } from '@/types/common';
import { preloadImage } from '@/utils/preloader';

interface LazyImageProps extends IntersectionObserverInit, Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string;
  loader?: ReactNode;
  isCircle?: boolean;
  objectFit?: string;
}

const LazyImage: FunctionComponent<LazyImageProps> = ({
  width = 100,
  height = 100,
  src,
  loader = <Loader />,
  isCircle,
  threshold,
  rootMargin,
  objectFit = 'contain',
  ...props
}) => {
  const [imageContainerElement, setImageContainerElement] = useState<Nullable<HTMLDivElement>>();
  const [imageSrc, setImageSrc] = useState<Nullable<string>>();
  const [freeze, setFreeze] = useState<boolean>(false);
  const entry = useIntersectionObserver({
    target: imageContainerElement,
    threshold,
    rootMargin,
    freeze,
  });

  useEffect(() => {
    const hasIOSupport = 'IntersectionObserver' in window;

    if (!hasIOSupport) {
      setImageSrc(src);
    } else {
      if (entry && entry.isIntersecting && !freeze) {
        preloadImage(src).then(() => {
          setFreeze(true);
          setImageSrc(src);
        });
      }
    }
  }, [entry, src]);

  return (
    <LazyImageContainer
      ref={(ref) => setImageContainerElement(ref)}
      width={width}
      height={height}
      isCircle={isCircle}
      hasImageSrc={!!imageSrc}
    >
      {loader && !imageSrc && <>{loader}</>}
      {imageSrc && (
        <LazyImageStyled {...props} width={width} height={height} src={imageSrc} loading='lazy' objectFit={objectFit} />
      )}
    </LazyImageContainer>
  );
};
export default LazyImage;

export const LazyImageContainer = styled.div<{
  width?: number | string;
  height?: number | string;
  isCircle?: boolean;
  hasImageSrc?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ width }) => `width: ${width}px; min-width: ${width}px;`}
  ${({ height }) => `height: ${height}px; min-height: ${height}px;`}
  border-radius: ${({ isCircle }) => (isCircle ? '50%' : '0')};
`;

export const LazyImageStyled = styled.img<{
  width?: number | string;
  height?: number | string;
  isCircle?: boolean;
  objectFit: string;
}>`
  display: block;
  max-height: 100%;
  text-indent: -9999px;
  opacity: 1;
  border-radius: 50%;
  ${({ theme, width, height, objectFit }) => `
      width: ${width}px; min-width: ${width}px;
      height: ${height}px; min-height: ${height}px;
      object-fit: ${objectFit};
      border: 1px solid ${theme.color.gray200};
    `}
`;
